<script>
import { h } from 'vue';
import FadeTransition from '@src/components/Transitions/Fade.vue';
import IntersectionObserver from '@src/components/IntersectionObserver/IntersectionObserver.vue';
/**
 * Wrapper used to anchor elements to the top, bottom, left, or right of the screen when
 * the user scrolls them out of view. You wrap the element you want to stick with this component,
 * and optionally provide a slot to be used when it is *stuck* to the edge of the screen.
 * If you do not provide a slot, then the original element will be used.
 */
export default {
  name: 'SbStickyElement',
  props: {
    /**
     * Percentage of the element used to determine when the intersect-enter and intersect-leave callbacks are fired.
     */
    threshold: {
      type: Number,
      default: 0.25,
    },
    /**
     * Where to anchor the stick section `top|left|right|bottom`.
     * The sticky container is full height/width.
     * To add dropshadow, you can use the class `sb-shadow` on your sticky slot.
     */
    position: {
      type: String,
      default: 'top',
      validator: (value) => {
        return value.match(/(top|left|right|bottom)/);
      },
    },
    disableSticky: {
      type: Boolean,
      default: false,
    },
    classes: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    showSticky: false,
  }),
  computed: {
    providedCustomStickySlot() {
      return this.$slots.sticky && this.$slots.sticky() !== undefined;
    },
    shouldShowSticky() {
      return this.showSticky && !this.disableSticky;
    },
  },
  methods: {
    handleIntersectEnter() {
      this.showSticky = false;
    },
    handleIntersectLeave() {
      this.showSticky = true;
    },
  },
  render() {
    const defaultSlots = this.$slots.default && this.$slots.default();
    const intersectionObserverChildren = [h('div', {}, defaultSlots)];
    const intersectionObserver = h(IntersectionObserver, {
      threshold: this.threshold,
      onIntersectEnter: this.handleIntersectEnter,
      onIntersectLeave: this.handleIntersectLeave,
    }, intersectionObserverChildren);

    const child = this.providedCustomStickySlot ? this.$slots.sticky() : defaultSlots;
    const stickySectionData = { class: [this.position, 'sb-sticky-section', ...this.classes.split(' ')] };
    const fadeTransitionChildren = this.shouldShowSticky ? [h('div', stickySectionData, child)] : [];
    const fadeTransition = h(FadeTransition, {}, fadeTransitionChildren);
    return h('div', {
      'data-sb-regression': 'sb-sticky-element',
    }, [intersectionObserver, fadeTransition]);
  },
};
</script>

<style lang='scss' scoped>
@import '@src/components/StickyElement/style/sb-sticky-element.scss';
</style>
